import { ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PasswordEyeDirective } from '@catch/directives';
import { ButtonComponent, InfoBannerComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { passwordPattern } from '../../../../../../assets/regex';
import { confirmField } from '../../../../../validators/confirmField';
import { PlatformAuthService } from '../../../../services/platform-auth.service';
import { PlatformTfaService } from '../../../../services/platform-tfa.service';
import { PlatformTfaAppModalComponent } from '../tfa-app-modal/platform-tfa-app-modal.component';

@Component({
  selector: 'app-platform-settings-password',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    PasswordEyeDirective,
    ButtonComponent,
    InfoBannerComponent,
    PlatformTfaAppModalComponent,
  ],
  templateUrl: './platform-settings-password.component.html',
  styleUrl: './platform-settings-password.component.scss',
})
export class PlatformSettingsPasswordComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(PlatformAuthService);
  private readonly tfaService = inject(PlatformTfaService);
  private readonly toastsService = inject(ToastsService);

  readonly tfaAppModal = viewChild.required<PlatformTfaAppModalComponent>('tfaAppModal');
  readonly hasPassword = this.auth.hasPassword;
  readonly submitAttempt = signal(false);
  readonly submitting = signal(false);
  readonly formEpoch = signal(0);

  passwordForm: FormGroup = this.buildForm(this.hasPassword());

  ngOnInit(): void {
    if (!this.tfaService.methods().length) {
      this.tfaService.loadMethods().subscribe();
    }
  }

  /**
   * Submits the password change or set-password form.
   */
  onSubmit(): void {
    this.submitAttempt.set(true);
    if (this.passwordForm.invalid) {
      return;
    }
    if (this.tfaService.methods().length > 0) {
      this.tfaAppModal().open();
      return;
    }
    this.persistPassword();
  }

  /**
   * Completes a password change after TOTP confirmation.
   * @param tfaCode - Authenticator code
   */
  onTfaSubmit(tfaCode: string): void {
    this.persistPassword(tfaCode);
  }

  /**
   * Sends the password change to the API.
   * @param tfaCode - Optional TOTP code
   */
  private persistPassword(tfaCode?: string): void {
    const settingNewPassword = !this.hasPassword();
    this.submitting.set(true);
    this.auth
      .changePassword(
        this.newPassword.value,
        settingNewPassword ? undefined : this.oldPassword?.value,
        tfaCode
      )
      .subscribe({
        next: () => {
          this.passwordForm = this.buildForm(true);
          this.formEpoch.update((epoch) => epoch + 1);
          this.submitAttempt.set(false);
          this.submitting.set(false);
          if (tfaCode) {
            this.tfaAppModal().close();
          }
          this.toastsService.success(
            settingNewPassword
              ? 'jobboard.platform.settings.password.set-toast'
              : 'jobboard.platform.settings.password.changed-toast'
          );
        },
        error: () => {
          if (tfaCode) {
            this.tfaAppModal().validate(false);
          } else {
            this.oldPassword?.setErrors({ incorrect: true });
          }
          this.submitting.set(false);
        },
      });
  }

  get oldPassword() {
    return this.passwordForm.get('oldPassword');
  }

  get newPassword() {
    return this.passwordForm.get('newPassword')!;
  }

  get newPasswordConfirm() {
    return this.passwordForm.get('newPasswordConfirm')!;
  }

  /**
   * Builds the password form for change or set-password.
   * @param hasPassword - Whether the account already has a password
   * @returns Password form group
   */
  private buildForm(hasPassword: boolean): FormGroup {
    const newPassword = this.fb.control(null, [
      Validators.required,
      Validators.pattern(passwordPattern),
    ]);
    const newPasswordConfirm = this.fb.control(null, [Validators.required]);
    if (!hasPassword) {
      return this.fb.group(
        {
          newPassword,
          newPasswordConfirm,
        },
        {
          validators: [confirmField('newPassword', 'newPasswordConfirm', 'newPasswordMismatch')],
        }
      );
    }
    return this.fb.group(
      {
        oldPassword: this.fb.control(null, [Validators.required]),
        newPassword,
        newPasswordConfirm,
      },
      {
        validators: [confirmField('newPassword', 'newPasswordConfirm', 'newPasswordMismatch')],
      }
    );
  }
}
